/**
 * useJourneyConfig Hook
 * Manages journey configuration data with hierarchical scope support
 * Fetches from database (rule_fragments table) using config service
 */

import { useCallback, useState } from 'react';
import { configEntityId } from '@/lib/debt-type-cohort';
import { getCompiledConfig, getRuleFragment, saveConfig } from '@/services/config';
import { type Client, type DebtType, Domain, type Operator, Scope } from '@/types';
import type { JourneyConfig } from '@/types/config/journey';
import { scopeCarriesPlanConfig } from '../lib/config-scope';
import { buildScopeKey, configScopeIds, getScopeType, useResetToOperatorDefaults } from './useResetToOperatorDefaults';

export interface UseJourneyConfigReturn {
  /**
   * Load config for Hub context entities (returns merged config)
   * Fetches from database and merges hierarchically
   */
  loadConfigForContext: (operator: Operator | null, debtType: DebtType | null, client: Client | null) => Promise<JourneyConfig>;

  /**
   * Load scope-specific config (unmerged, just this scope's rules)
   * Returns null if no config exists at this specific scope
   */
  loadScopeConfig: (operator: Operator | null, debtType: DebtType | null, client: Client | null) => Promise<JourneyConfig | null>;

  /**
   * Save config for Hub context entities and refresh compiled config
   * @param replace - If true, replace the entire fragment instead of merging (used for reset operations that need to delete keys)
   * @returns The freshly compiled config after save
   */
  saveConfigForContext: (
    operator: Operator | null,
    debtType: DebtType | null,
    client: Client | null,
    config: Partial<JourneyConfig>,
    options?: { replace?: boolean }
  ) => Promise<JourneyConfig>;

  /**
   * Reset config at client/debtType level to inherit from operator defaults
   * Deletes the rule fragment at the current scope, causing it to fall back to operator config
   * @returns The operator-level config (now inherited)
   */
  resetToOperatorDefaults: (operator: Operator | null, debtType: DebtType | null, client: Client | null) => Promise<JourneyConfig>;
}

/**
 * Hook for managing journey configuration data
 * Fetches from database using config service
 */
export function useJourneyConfig(): UseJourneyConfigReturn {
  const [configs, setConfigs] = useState<Record<string, JourneyConfig>>({});

  /**
   * Load config for Hub context entities
   * Uses compiled_config_for which handles hierarchical merging automatically
   * Structure: system → operator → client → debt_type (most specific wins)
   */
  const loadConfigForContext = useCallback(
    async (operator: Operator | null, debtType: DebtType | null, client: Client | null): Promise<JourneyConfig> => {
      // Start with empty config
      let config: JourneyConfig = {};

      if (!operator) {
        return config;
      }

      // Check cache first
      const scopeKey = buildScopeKey(operator.id, client?.id, debtType?.id);
      const cachedConfig = configs[scopeKey];
      if (cachedConfig !== undefined) {
        return cachedConfig;
      }

      // Determine most specific entity ID (debt_type > client > operator)
      const entityId = configEntityId(debtType, client, operator.id);

      try {
        // Fetch compiled config for journey domain (already hierarchically merged by DB)
        const journeyConfig = await getCompiledConfig(entityId, Domain.JOURNEY);

        if (journeyConfig?.config_json) {
          config = journeyConfig.config_json as JourneyConfig;
        }

        // Update cache
        setConfigs((prev) => ({
          ...prev,
          [scopeKey]: config,
        }));

        return config;
      } catch (error) {
        console.error('[useJourneyConfig] Error loading config from database:', error);
        // Fallback to cached config or empty config on error
        if (cachedConfig !== undefined) {
          return cachedConfig;
        }
        return config;
      }
    },
    [configs]
  );

  /**
   * Save config for Hub context entities
   * Saves to database and refreshes compiled config
   * @returns The freshly compiled config after save
   */
  const saveConfigForContext = useCallback(
    async (
      operator: Operator | null,
      debtType: DebtType | null,
      client: Client | null,
      config: Partial<JourneyConfig>,
      options?: { replace?: boolean }
    ): Promise<JourneyConfig> => {
      if (!operator) {
        console.warn('Cannot save config: no operator selected');
        return {};
      }

      try {
        const scope = getScopeType(debtType, client);
        const scopeKey = buildScopeKey(operator.id, client?.id, debtType?.id);

        // Load existing fragment to preserve fields managed by other pages
        // Skip merge when replace=true (used for reset operations that need to delete keys)
        let rulesToSave: JourneyConfig;
        if (options?.replace) {
          rulesToSave = { ...(config as JourneyConfig) };
        } else {
          const ownerId = configEntityId(debtType, client, operator.id);
          const existingFragment = await getRuleFragment(Domain.JOURNEY, scope, ownerId);
          const existingRules = (existingFragment?.rules as JourneyConfig) || {};
          rulesToSave = { ...existingRules, ...config };
          // Deep-merge payment_settings: paymentConfigToJourneyConfig omits sections the user
          // did not touch (refunds, budget assessment) so they keep inheriting, and a shallow
          // spread would read that omission as a deletion of an override stored earlier.
          if (config.payment_settings || existingRules.payment_settings) {
            rulesToSave.payment_settings = {
              ...existingRules.payment_settings,
              ...config.payment_settings,
            };
          }
        }

        // payment_plan_tiers and budget_assessment are only meaningful at operator/client scope; strip
        // them at lower scopes so a stale fragment can't shadow the inherited value.
        if (!scopeCarriesPlanConfig(scope)) {
          delete rulesToSave.payment_plan_tiers;
          delete rulesToSave.budget_assessment;
        }

        // Legacy ticket fields used to live on journey; they moved to governance
        // (seed 0125). Merge-saves still re-submit them and trip extra="forbid".
        // Drop from the outbound payload only — not a general unknown-key
        // stripper. replace:false deep-merge leaves orphans in DB; use replace when
        // we stripped so the stored fragment converges on first successful save.
        const legacyTicketKeys = ['queries_enabled', 'query_categories', 'tickets_enabled', 'ticket_categories'] as const;
        const strippedLegacyTickets = legacyTicketKeys.some((key) => key in rulesToSave);
        for (const key of legacyTicketKeys) delete (rulesToSave as Record<string, unknown>)[key];

        const replace = (options?.replace ?? false) || strippedLegacyTickets;

        // Save config
        await saveConfig({
          domain: Domain.JOURNEY,
          scope,
          rules: rulesToSave as JourneyConfig,
          replace,
          operatorId: operator.id,
          ...configScopeIds(debtType, client),
          notes: 'Journey configuration',
        });

        // Invalidate cache before refreshing to ensure we get fresh data
        setConfigs((prev) => {
          const updated = { ...prev };
          delete updated[scopeKey];
          return updated;
        });

        // Refresh compiled config after save (this will update cache with fresh data)
        const refreshedConfig = await loadConfigForContext(operator, debtType, client);
        return refreshedConfig;
      } catch (error) {
        console.error('Error saving configuration:', error);
        throw error;
      }
    },
    [loadConfigForContext]
  );

  /**
   * Load scope-specific config (unmerged, just this scope's rules)
   * Returns null if no config exists at this specific scope
   */
  const loadScopeConfig = useCallback(
    async (operator: Operator | null, debtType: DebtType | null, client: Client | null): Promise<JourneyConfig | null> => {
      if (!operator) return null;

      const scope = getScopeType(debtType, client);
      let ownerId: string | undefined;

      if (scope === Scope.CLIENT && client) {
        ownerId = client.id;
      } else if (scope === Scope.DEBT_TYPE && debtType) {
        ownerId = debtType.id;
      } else if (scope === Scope.OPERATOR) {
        ownerId = operator.id;
      }

      try {
        const fragment = await getRuleFragment(Domain.JOURNEY, scope, ownerId);
        if (fragment?.rules) {
          return fragment.rules as JourneyConfig;
        }
        return null;
      } catch (error) {
        console.error('[useJourneyConfig] Error loading scope config:', error);
        return null;
      }
    },
    []
  );

  /**
   * Clear a cache entry by scope key
   */
  const clearCacheEntry = useCallback((scopeKey: string) => {
    setConfigs((prev) => {
      const next = { ...prev };
      delete next[scopeKey];
      return next;
    });
  }, []);

  /**
   * Reset config at client/debtType level to inherit from operator defaults
   * Uses shared utility hook
   */
  const resetToOperatorDefaults = useResetToOperatorDefaults<JourneyConfig>(Domain.JOURNEY, loadConfigForContext, clearCacheEntry);

  return {
    loadConfigForContext,
    loadScopeConfig,
    saveConfigForContext,
    resetToOperatorDefaults,
  };
}
